/**
 * 테마(다크모드) 및 Ant Design 설정을 관리하는 Provider입니다.
 *
 * 주요 기능:
 * - 다크모드 토글 (localStorage 저장)
 * - Ant Design ConfigProvider 설정
 * - 한국어 로케일 적용
 * - 컴포넌트별 테마 커스터마이징
 *
 * @remarks
 * 다크모드 설정은 localStorage에 저장되어 새로고침 후에도 유지됩니다.
 * 로그인 페이지 등 특정 페이지에서는 다크모드가 비활성화됩니다.
 */
// import { trpc } from '../common/trpc';
import { StyleProvider } from '@ant-design/cssinjs';
import { ConfigProvider, theme } from 'antd';
import koKR from 'antd/lib/locale/ko_KR';
import enUS from 'antd/lib/locale/en_US';
import { useLocalStorage } from '../hooks/use-local-storage';
import { useLocale } from '../hooks/use-locale';
import type { SupportedLocale } from '../locales/types';
import type { Locale } from 'antd/lib/locale';

/** Ant Design locale 맵 */
const antdLocaleMap: Record<SupportedLocale, Locale> = {
  ko: koKR,
  en: enUS,
};
import { createContextProvider } from '../common/create-context';
import { ACCENT_COLOR, BORDER_COLOR, BORDER_COLOR_DARK, FONT_SIZE } from '../common/constant';
import { useEffect } from 'react';
import { useRouter } from 'next/router';
const { defaultAlgorithm, darkAlgorithm } = theme;

/** 다크모드 사용하지 않는 페이지 목록 */
const ignoreDarkModePath = ['/', '/license-select'];

const [useThemeContext, Provider] = createContextProvider<{
  isDarkMode: boolean;
  setIsDarkMode: (newMode: boolean) => void;
}>();

/**
 * 테마(다크모드) 및 Ant Design 설정을 관리하는 Provider.
 * 다크모드 토글, 다국어 로케일 적용, 컴포넌트별 테마 커스터마이징을 제공합니다.
 * 다크모드 설정은 localStorage에 저장되어 새로고침 후에도 유지됩니다.
 */
const ThemeProvider = ({ children }: { children: React.ReactNode }) => {
  const [isDarkModeFromStorage, setIsDarkMode] = useLocalStorage('darkMode', false);
  const router = useRouter();
  // 특정 페이지 예외 적용 후 다크모드 최종 결정
  const isDarkMode = isDarkModeFromStorage && !ignoreDarkModePath.includes(router.pathname);

  // 현재 선택된 언어에 따라 Ant Design 로케일 설정
  const { currentLocale } = useLocale();
  const antdLocale = antdLocaleMap[currentLocale];

  useEffect(() => {
    // ✅ 클라이언트 사이드에서만 실행되도록 체크
    if (typeof window !== 'undefined') {
      if (isDarkMode) {
        document.documentElement.classList.add('dark');
        document.documentElement.style.colorScheme = 'dark';
      } else {
        document.documentElement.classList.remove('dark');
        document.documentElement.style.colorScheme = 'light';
      }
    }
  }, [isDarkMode]);

  return (
    <Provider value={{ isDarkMode, setIsDarkMode }}>
      <StyleProvider layer>
        <ConfigProvider
          locale={antdLocale}
          theme={{
            cssVar: { prefix: 'ant', key: 'engview' },
            hashed: false,
            algorithm: isDarkMode ? darkAlgorithm : defaultAlgorithm,
            token: {
              // AntD Design Token에는 css variable을 이용하여 색상을 지정할 수 없음. algorithm 함수에서 색상 조합을 시도하려하기 때문.
              colorPrimary: /* isDarkMode ? PRIMARY_COLOR_DARK : */ ACCENT_COLOR, // 모든 컴포넌트들의 활성 상태 색상을 테마색(주황색)으로 지정
              // colorInfo: isDarkMode ? INFO_COLOR_DARK : INFO_COLOR, // 모든 컴포넌트들의 정보 상태 색상을 테마색(연회색)으로 지정
              // colorBorder: '#A6A6A6',
              // colorPrimaryBorder: isDarkMode ? BORDER_COLOR_DARK : BORDER_COLOR,
              fontFamily: 'var(--fontFamily)',
              fontSize: FONT_SIZE,
              colorBorder: isDarkMode ? BORDER_COLOR_DARK : BORDER_COLOR,
              // 탭 메뉴 우측 탭이 없는 여백 아래의 경계선 색상
              colorBorderSecondary: isDarkMode ? BORDER_COLOR_DARK : BORDER_COLOR,
            },
            components: {
              /**
               * 위 token.colorPrimary 색상에 의해서 기본 테마 색상이 모든 컴포넌트들에 대해 기본 적용됨
               * 예외를 적용하려면 각 컴포넌트별로 재지정하면 됨
               */
              Button: {
                defaultHoverBg: 'var(--accentTint)',
                defaultHoverColor: isDarkMode ? '#ffffffd9' : '#000000e0',
                defaultHoverBorderColor: isDarkMode ? BORDER_COLOR_DARK : BORDER_COLOR,
                defaultActiveBg: 'var(--accentTint)',
                defaultActiveColor: isDarkMode ? '#ffffffd9' : '#000000e0',
                defaultActiveBorderColor: isDarkMode ? BORDER_COLOR_DARK : BORDER_COLOR,
              },
              // Input: {
              //   colorPrimary: PRIMARY_COLOR,
              //   // algorithm: true, // Enable algorithm
              // },
              // InputNumber: {
              //   colorPrimary: PRIMARY_COLOR,
              //   // algorithm: true, // Enable algorithm
              // },
              Select: {
                hoverBorderColor: isDarkMode ? BORDER_COLOR_DARK : BORDER_COLOR,
                activeBorderColor: isDarkMode ? BORDER_COLOR_DARK : BORDER_COLOR,
                selectorBg: isDarkMode ? '#000000' : '#ffffff',
                optionSelectedBg: 'var(--accentTint)',
                optionActiveBg: 'var(--accentTint)',
                activeOutlineColor: 'transparent',
              },
              // Radio: {
              //   colorPrimary: PRIMARY_COLOR,
              //   // algorithm: true, // Enable algorithm
              // },
              // Checkbox: {
              //   colorPrimary: PRIMARY_COLOR,
              //   // algorithm: true, // Enable algorithm
              // },
              // Switch: {
              //   colorPrimary: PRIMARY_COLOR,
              //   // algorithm: true, // Enable algorithm
              // },
              Tabs: {
                inkBarColor: ACCENT_COLOR,
                itemActiveColor: isDarkMode ? '#ffffffd9' : '#000000e0',
                itemSelectedColor: isDarkMode ? '#ffffffd9' : '#000000e0',
                itemHoverColor: isDarkMode ? '#ffffffd9' : '#000000e0',
                horizontalMargin: '0',
              },
              // Calendar: {
              //   colorPrimary: PRIMARY_COLOR,
              //   // algorithm: true, // Enable algorithm
              // },
              // DatePicker: {
              //   colorPrimary: PRIMARY_COLOR,
              //   // algorithm: true, // Enable algorithm
              // },
              // Table: {
              //   colorPrimary: PRIMARY_COLOR,
              //   // algorithm: true, // Enable algorithm
              // },
              // Modal: {
              //   colorPrimary: PRIMARY_COLOR,
              //   // algorithm: true, // Enable algorithm
              // },
              // Popover: {
              //   colorPrimary: PRIMARY_COLOR,
              //   // algorithm: true, // Enable algorithm
              // },
              // Tooltip: {
              //   colorPrimary: PRIMARY_COLOR,
              //   // algorithm: true, // Enable algorithm
              // },
              // Drawer: {
              //   colorPrimary: PRIMARY_COLOR,
              //   // algorithm: true, // Enable algorithm
              // },
              // Alert: {
              //   colorPrimary: PRIMARY_COLOR,
              //   // algorithm: true, // Enable algorithm
              // },
              // Progress: {
              //   colorPrimary: PRIMARY_COLOR,
              //   // algorithm: true, // Enable algorithm
              // },
              // Spin: {
              //   colorPrimary: PRIMARY_COLOR,
              //   // algorithm: true, // Enable algorithm
              // },
              // Skeleton: {
              //   colorPrimary: PRIMARY_COLOR,
              //   // algorithm: true, // Enable algorithm
              // },
              // Avatar: {
              //   colorPrimary: PRIMARY_COLOR,
              //   // algorithm: true, // Enable algorithm
              // },
              // Tag: {
              //   colorPrimary: PRIMARY_COLOR,
              //   // algorithm: true, // Enable algorithm
              // },
              // Badge: {
              //   colorPrimary: PRIMARY_COLOR,
              //   // algorithm: true, // Enable algorithm
              // },
              // Timeline: {
              //   colorPrimary: PRIMARY_COLOR,
              //   // algorithm: true, // Enable algorithm
              // },
              // Card: {
              //   colorPrimary: PRIMARY_COLOR,
              //   // algorithm: true, // Enable algorithm
              // },
              // Collapse: {
              //   colorPrimary: PRIMARY_COLOR,
              //   // algorithm: true, // Enable algorithm
              // },
              // Dropdown: {
              //   colorPrimary: PRIMARY_COLOR,
              //   // algorithm: true, // Enable algorithm
              // },
              Pagination: {
                // 주문 내역 상단 페이징 버튼이 연회색 배경 위에 있는데, 단추 배경이 흰색이면 보기 좋지 않아서 기본 투명 배경으로 바꿈
                itemBg: 'transparent',
              },
              // Steps: {
              //   colorPrimary: PRIMARY_COLOR,
              //   // algorithm: true, // Enable algorithm
              // },
              // Cascader: {
              //   colorPrimary: PRIMARY_COLOR,
              //   // algorithm: true, // Enable algorithm
              // },
              // Transfer: {
              //   colorPrimary: PRIMARY_COLOR,
              //   // algorithm: true, // Enable algorithm
              // },
              // TreeSelect: {
              //   colorPrimary: PRIMARY_COLOR,
              //   // algorithm: true, // Enable algorithm
              // },
              // Upload: {
              //   colorPrimary: PRIMARY_COLOR,
              //   // algorithm: true, // Enable algorithm
              // },
              // Rate: {
              //   colorPrimary: PRIMARY_COLOR,
              //   // algorithm: true, // Enable algorithm
              // },
              // Slider: {
              //   colorPrimary: PRIMARY_COLOR,
              //   // algorithm: true, // Enable algorithm
              // },
              // Form: {
              //   colorPrimary: PRIMARY_COLOR,
              //   // algorithm: true, // Enable algorithm
              // },
              // Tree: {
              //   colorPrimary: PRIMARY_COLOR,
              //   // algorithm: true, // Enable algorithm
              // },
              // List: {
              //   colorPrimary: PRIMARY_COLOR,
              //   // algorithm: true, // Enable algorithm
              // },
              // Popconfirm: {
              //   colorPrimary: PRIMARY_COLOR,
              //   // algorithm: true, // Enable algorithm
              // },
              // Empty: {
              //   colorPrimary: PRIMARY_COLOR,
              //   // algorithm: true, // Enable algorithm
              // },
              // Result: {
              //   colorPrimary: PRIMARY_COLOR,
              //   // algorithm: true, // Enable algorithm
              // },
              // Descriptions: {
              //   colorPrimary: PRIMARY_COLOR,
              //   // algorithm: true, // Enable algorithm
              // },
            },
          }}
          modal={{ okButtonProps: { type: 'default' } }}
        >
          {children}
        </ConfigProvider>
      </StyleProvider>
    </Provider>
  );
};

export { useThemeContext, ThemeProvider };
